import * as React from "react";
import type { Money } from "@/lib/api/types";
import { cn } from "@/lib/utils";
import { CounterButton } from "./Button";
import { Amount } from "./Money";

/**
 * The order line reads like a ledger page (DESIGN.md §1, §7). This is the
 * typographic discipline that survived Ledger Ink's retirement: right-aligned
 * tabular figures, a single rule above the subtotal, a double rule above the
 * total, tax as native rows, round-off shown.
 *
 * Every pack builds its bill from these three rows, so a restaurant folio, a
 * clinic invoice and a retail sale total the same way.
 */
export function LedgerLine({
  label,
  meta,
  value,
  saving = false,
  rule = "none",
  actions,
  className,
}: Readonly<{
  label: React.ReactNode;
  /** Modifiers, timestamps, the tax rate: 14 px, text.weak. */
  meta?: React.ReactNode;
  value: Money | undefined | null;
  saving?: boolean;
  /** `single` above the subtotal, `double` above the total. */
  rule?: "none" | "single" | "double";
  actions?: React.ReactNode;
  className?: string;
}>) {
  return (
    <div
      className={cn(
        "flex min-h-[56px] items-center justify-between gap-4 py-2",
        rule === "single" && "border-t border-stroke-strong",
        rule === "double" && "border-t-[3px] border-double border-stroke-strong",
        className,
      )}
    >
      <div className="min-w-0 flex-1">
        {/* Item names wrap to two lines; they are never truncated (DESIGN.md §5). */}
        <div className="font-text text-lg text-text-strong">{label}</div>
        {meta ? <div className="font-sans text-sm text-text-weak">{meta}</div> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-1">{actions}</div> : null}
      <Amount value={value} scale="line" saving={saving} className="shrink-0 text-right" />
    </div>
  );
}

/** The bill total: the one 32 px figure in the order line. */
export function LedgerTotal({
  label = "Total",
  value,
  className,
}: Readonly<{ label?: React.ReactNode; value: Money | undefined | null; className?: string }>) {
  return (
    <div
      className={cn(
        "flex items-baseline justify-between gap-4 border-t-[3px] border-double border-stroke-strong pt-3",
        className,
      )}
    >
      <span className="font-sans text-base font-semibold text-text-strong">{label}</span>
      <Amount value={value} scale="total" strong />
    </div>
  );
}

/**
 * An applied tender or reward (DESIGN.md §7). This replaces the oxblood stamp:
 * a honey-100 row with honey-800 text, the amount tabular right, and Remove as
 * a text button. Nothing rotates and nothing is stamped.
 */
export function AppliedRow({
  label,
  value,
  icon,
  onRemove,
  removeLabel = "Remove",
  className,
}: Readonly<{
  label: React.ReactNode;
  value: Money | undefined | null;
  icon?: React.ReactNode;
  onRemove?: () => void;
  removeLabel?: string;
  className?: string;
}>) {
  return (
    <div
      className={cn(
        "flex min-h-target items-center justify-between gap-3 rounded-tile bg-fill-accent-weak px-3 py-2",
        className,
      )}
    >
      <span className="flex min-w-0 items-center gap-2 font-sans text-base font-semibold text-honey-800">
        {icon ? <span className="shrink-0 text-honey-800">{icon}</span> : null}
        <span className="truncate">{label}</span>
      </span>
      <span className="flex shrink-0 items-center gap-3">
        <Amount value={value} scale="line" saving />
        {onRemove ? (
          <CounterButton tone="quiet" size="dense" onClick={onRemove}>
            {removeLabel}
          </CounterButton>
        ) : null}
      </span>
    </div>
  );
}

/**
 * The summary rows under the order line — subtotal, each tax at its own rate,
 * round-off, the amount paid. A label, a leader, a right-aligned figure.
 *
 * Separate from `LedgerLine` on purpose: an order line is a 56 px touch target
 * because a cashier edits it, a summary row is read and never tapped, so it
 * stays compact. Both keep the same tabular right edge, which is the whole
 * point of a ledger.
 *
 * `value` is the server's display string (or a `Money`) — never a figure this
 * component computed.
 */
export function LedgerSummaryRow({
  label,
  value,
  saving = false,
  strong = false,
  mark,
  className,
}: Readonly<{
  label: React.ReactNode;
  value: Money | string | number | undefined | null;
  /** A deduction: honey with a leading minus, never red (DESIGN.md §3). */
  saving?: boolean;
  strong?: boolean;
  /** A short word beside the label — "applied", "paid", "waived". */
  mark?: React.ReactNode;
  className?: string;
}>) {
  const plain = typeof value === "string" || typeof value === "number";
  return (
    <div className={cn("flex items-baseline gap-2 py-1", className)}>
      <span
        className={cn(
          "shrink-0 font-sans text-sm",
          strong ? "font-semibold text-text-strong" : "text-text-weak",
        )}
      >
        {label}
      </span>
      {mark ? (
        <span className="shrink-0 font-sans text-sm font-semibold uppercase tracking-[0.06em] text-text-accent">
          {mark}
        </span>
      ) : null}
      {/* The leader carries the eye across to the figure; it is the one piece of
          the ledger page that survived, and it is a rule, not a decoration. */}
      <span className="min-w-3 flex-1 translate-y-[-3px] border-b border-dotted border-stroke-weak" aria-hidden="true" />
      {plain ? (
        <span
          className={cn(
            "font-ledger tabular-nums",
            strong ? "text-lg font-semibold" : "text-base",
            saving ? "text-text-accent" : "text-text-strong",
          )}
        >
          {saving ? "\u2212" : ""}
          {value}
        </span>
      ) : (
        <Amount value={value} scale="inherit" saving={saving} strong={strong} />
      )}
    </div>
  );
}
